import type { MaintenanceControl, VersionPolicyControl } from "../types";

export type AppAccessTone = "healthy" | "critical" | "unknown";

export interface AppAccessSummary {
  tone: AppAccessTone;
  headline: string;
  detail: string;
}

/**
 * Answers the only question the page really has to answer: can somebody open the
 * current app right now? Maintenance mode wins over the version allowlist
 * because the mobile app blocks on it first.
 */
export function summarizeAppAccess(
  maintenance: MaintenanceControl | null,
  policy: VersionPolicyControl | null
): AppAccessSummary {
  if (!maintenance || !policy) {
    return { tone: "unknown", headline: "Checking the app…", detail: "Reading the current app settings." };
  }
  if (maintenance.enabled) {
    return {
      tone: "critical",
      headline: "Nobody can use the app",
      detail: "The app is paused, so everyone who opens it sees the maintenance message and cannot get past it."
    };
  }
  if (!policy.configured || policy.allowedVersions.length === 0) {
    return {
      tone: "healthy",
      headline: "Everyone can use the app",
      detail: "The app is not paused and no version check is switched on, so any deployed build can get in."
    };
  }
  const count = policy.allowedVersions.length;
  return {
    tone: "healthy",
    headline: `Only ${count === 1 ? "one app version" : `${count} app versions`} can be used`,
    detail: `The app is not paused. People on ${listVersions(policy.allowedVersions)} can get in; everyone else is asked to update first.`
  };
}

export function listVersions(versions: string[]): string {
  if (versions.length <= 2) return versions.join(" and ");
  return `${versions.slice(0, -1).join(", ")} and ${versions[versions.length - 1]}`;
}
